import Icon from 'components/_UI/Icon';
import Text from 'components/_UI/Text';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import paths from 'routes/paths';
import styled from 'styled-components';
import colors from 'theme/colors';
import { User, MapPin, LogIn, Map, Search, List, LayoutDashboard } from 'lucide-react';
import { useAuth } from 'context/auth/Auth';
import { useUser } from 'context/user/User';
import { useShowSearchOverlay } from 'context/general/ShowSearchOverlay';
import Loader from 'components/_UI/Loader';
import breakpoints from 'theme/breakpoints';
import LinkPage from 'components/_UI/LinkPage';
import { Column } from 'styled/alignment/Column';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import useProducerPanelEntry from 'modules/dashboard/producer-access/useProducerPanelEntry';
import { actorHasOwnPanel } from 'helpers/dashboardAccess';
import { openEventsPanel } from 'modules/dashboard/producer-access/enterProducerPanel';

export default function Navbar({ onMapToggle, isMapView }) {
  const { isAuth } = useAuth();
  const { user } = useUser();
  const { setShowSearchOverlay } = useShowSearchOverlay();
  const { hasAccess: hasProducerPanelAccess, openPanel: openProducerPanel, picker: producerPanelPicker } = useProducerPanelEntry();

  const location = useLocation();

  const [selectedIcon, setSelectedIcon] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false)
    }, 1000);
  }, [])


  useEffect(() => {
    switch (location.pathname) {
      case paths.events.home:
        setSelectedIcon('home');
        break;
      case paths.user.profile:
        setSelectedIcon('profile');
        break;
      case paths.login:
        setSelectedIcon('login');
        break;
      case paths.dashboard.establishment.profile:
        setSelectedIcon('store');
        break;
      case paths.user.wallet:
        setSelectedIcon('wallet');
        break;
      default:
        break;
    }
  }, [location.pathname]);

  if (isMapView) return null;

  return (
    <>
      <NavbarContainer>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <ToLink to={paths.events.home} isSelected={selectedIcon === 'home'}>
              <IconContainer>
                <MapPin color={selectedIcon === 'home' ? colors.primary : colors.primaryDashboard} />
              </IconContainer>
              <Text color={selectedIcon === 'home' ? colors.primary : colors.primaryDashboard}>
                Explorar
              </Text>
            </ToLink>

            {onMapToggle && (
              <MapToggleButton onClick={onMapToggle} isSelected={isMapView}>
                <IconContainer>
                  {isMapView
                    ? <List color={isMapView ? colors.primary : colors.primaryDashboard} />
                    : <Map color={isMapView ? colors.primary : colors.primaryDashboard} />
                  }
                </IconContainer>
                <Text color={isMapView ? colors.primary : colors.primaryDashboard}>
                  {isMapView ? 'Lista' : 'Mapa'}
                </Text>
              </MapToggleButton>
            )}

            {onMapToggle && (
              <MapToggleButton onClick={() => setShowSearchOverlay(true)}>
                <IconContainer>
                  <Search color={colors.primaryDashboard} />
                </IconContainer>
                <Text color={colors.primaryDashboard}>
                  Buscar
                </Text>
              </MapToggleButton>
            )}

            {isAuth && (
              (user?.role?.id != import.meta.env.VITE_PRODUCER_ROLE_ID && user?.role?.id != import.meta.env.VITE_ESTABLISHMENT_ROLE_ID) && (
                <ToLink to={paths?.user?.wallet} isSelected={selectedIcon === 'wallet'}>
                  <IconContainer>
                    <Icon name="wallet" color={selectedIcon === 'wallet' ? colors.primary : colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={selectedIcon === 'wallet' ? colors.primary : colors.primaryDashboard}>
                    Carteira
                  </Text>
                </ToLink>
              )
            )}

            {
              isAuth && (user?.role?.id == import.meta.env.VITE_ESTABLISHMENT_ROLE_ID || user?.role?.id == import.meta.env.VITE_ADMIN_ROLE_ID) && (
                <ToLink to={paths.dashboard.home} isSelected={selectedIcon === 'store'}>
                  <IconContainer>
                    <Icon name='store' color={selectedIcon === 'store' ? colors.primary : colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={selectedIcon === 'store' ? colors.primary : colors.primaryDashboard}>
                    Estabelecimento
                  </Text>
                </ToLink>
              )
            }

            {
              isAuth && (user?.role?.id == import.meta.env.VITE_PRODUCER_ROLE_ID || user?.role?.id == import.meta.env.VITE_ADMIN_ROLE_ID) && (
                <ToLink to={paths.dashboard.statistics} isSelected={selectedIcon === 'profile'}>
                  <IconContainer>
                    <Icon name='profile' color={selectedIcon === 'profile' ? colors.primary : colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={selectedIcon === 'profile' ? colors.primary : colors.primaryDashboard}>
                    Painel do produtor
                  </Text>
                </ToLink>
              )
            }


            {isAuth ? (
              (user?.role?.id != import.meta.env.VITE_ESTABLISHMENT_ROLE_ID && user?.role?.id != import.meta.env.VITE_PRODUCER_ROLE_ID && user?.role?.id != import.meta.env.VITE_ADMIN_ROLE_ID) && (
                <ToLink to={paths.user.profile} isSelected={selectedIcon === 'profile'}>
                  <IconContainer>
                    <User color={selectedIcon === 'profile' ? colors.primary : colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={selectedIcon === 'profile' ? colors.primary : colors.primaryDashboard}>
                    Perfil
                  </Text>
                </ToLink>
              )
            ) : (
              <DirectSignin>
                <LoginButton onClick={() => setIsLoginModalOpen(true)}>
                  <IconContainer>
                    <LogIn color={colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={colors.primaryDashboard}>
                    Entrar ou criar conta
                  </Text>
                </LoginButton>
              </DirectSignin>
            )}

            {
              isAuth && hasProducerPanelAccess && (
                <MapToggleButton onClick={openProducerPanel}>
                  <IconContainer>
                    <LayoutDashboard color={colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={colors.primaryDashboard}>
                    {actorHasOwnPanel(user) ? 'Painéis de equipe' : 'Painel do produtor'}
                  </Text>
                </MapToggleButton>
              )
            }

            {
              isAuth && user?.is_event_collaborator && !hasProducerPanelAccess && !actorHasOwnPanel(user) && (
                <MapToggleButton onClick={openEventsPanel}>
                  <IconContainer>
                    <LayoutDashboard color={colors.primaryDashboard} />
                  </IconContainer>
                  <Text color={colors.primaryDashboard}>
                    Painel de eventos
                  </Text>
                </MapToggleButton>
              )
            }
          </>
        )
        }



      </NavbarContainer>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      {producerPanelPicker}
    </>
  );
}

const NavbarContainer = styled.nav`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 60px;
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 4px 0;
  align-items: stretch;
  background: ${colors.neutralLight};
  border: 1px solid ${colors.occasionalGrey};
  z-index: 20;

  @media screen and (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const IconContainer = styled.button`
  width: 28px;
  height: 28px;
  justify-content: center;
  flex-direction: row;
  background: ${colors.neutralLight};
`;

const ToLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected',
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  padding: 2px 4px;
  & > p {
    margin-top: 2px;
    font-size: 10px;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
  }
  color: ${({ isSelected }) => (isSelected ? colors.primary : colors.primaryDashboard)};
`;

const DirectSignin = styled(Column)`
  flex: 1;
  min-width: 0;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  & > a >  p {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 0;
  }
  & > a >  p > p {
    margin-top: 2px;
    font-size: 10px;
  }
  & > a  {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  color: ${({ isSelected }) => (isSelected ? colors.primary : colors.primaryDashboard)};
`;

const MapToggleButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  padding: 2px 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ isSelected }) => (isSelected ? colors.primary : colors.primaryDashboard)};

  & > p {
    margin-top: 2px;
    font-size: 10px;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
  }
`;

const LoginButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  width: 100%;

  & > p {
    margin-top: 2px;
    font-size: 10px;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
  }
`;
